import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import useReveal from "src/hooks/use-reveal";
import SectionBackdrop from "./section-backdrop";
import { SECTION_IMAGES } from "src/config/images";

// РАЗВИЛКА: ЧЕТЫРЕ ПУТИ С ГЛАВНОЙ.
//
// Главная страница отвечает на вопрос «что это за компания». Дальше пути
// расходятся, и они разные по намерению: одному нужен каталог, второму —
// отдельная услуга вроде визы, третий проверяет, кто мы такие, четвёртый
// просто хочет спросить. Шапка это всё вмещает, но шапку не читают —
// её видят. Поэтому в конце главной стоит явная развилка.
//
// Порядок карточек — по частоте намерения, а не по алфавиту: сначала туры
// (за ними и пришли), потом услуги, потом «кто вы», потом вопросы.
//
// Ссылки — <Link>, а не <a>: переход остаётся клиентским, страница не
// перезагружается, а адрес по-прежнему можно скопировать и открыть в новой
// вкладке.

const LINKS = [
  { to: "/activities", key: "tours" },
  { to: "/services", key: "services" },
  { to: "/reviews", key: "reviews" },
  { to: "/about", key: "about" },
  { to: "/contacts", key: "contacts" },
];

export default function ExploreNav() {
  const { t } = useTranslation();
  const reveal = useReveal();

  return (
    <section
      ref={reveal.ref}
      className={`relative w-full max-w-[1526px] mx-auto px-4 py-10 sm:py-16 ${reveal.className}`}
      aria-labelledby="explore-nav-title"
    >
      {/* Фото во всю ширину окна — выбирается в src/config/images.js */}
      <SectionBackdrop image={SECTION_IMAGES.explore} />

      <div className="relative flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <p className="on-photo text-xs sm:text-sm font-extrabold uppercase tracking-[0.28em] text-kj-gold">
            {t("nav.eyebrow")}
          </p>
          <h2
            id="explore-nav-title"
            className="on-photo mt-3 text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-kj-ink text-balance"
          >
            {t("nav.title")}
          </h2>
        </div>
        <p className="on-photo text-sm sm:text-base text-kj-sub leading-relaxed max-w-[46ch]">
          {t("nav.subtitle")}
        </p>
      </div>

      <ul className="relative mt-7 sm:mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {LINKS.map(({ to, key }, i) => (
          <li key={to}>
            <Link
              to={to}
              className="group flex h-full flex-col rounded-2xl border border-kj-line bg-kj-dark/55 backdrop-blur-md p-5 sm:p-6 transition-colors duration-300 hover:border-kj-gold/60 hover:bg-kj-dark/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold focus-visible:ring-offset-2 focus-visible:ring-offset-kj-dark"
            >
              <span className="flex items-start justify-between gap-3">
                <span className="text-xs font-extrabold tabular-nums tracking-[0.2em] text-kj-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <ArrowUpRight
                  aria-hidden="true"
                  className="h-5 w-5 flex-none text-kj-sky transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </span>
              <span className="mt-4 block text-lg sm:text-xl font-extrabold uppercase tracking-tight text-kj-ink leading-snug">
                {t(`nav.${key}Title`)}
              </span>
              <span className="mt-2 block text-sm text-kj-sub leading-relaxed">
                {t(`nav.${key}Text`)}
              </span>
              <span className="mt-5 block text-xs font-bold uppercase tracking-[0.14em] text-kj-sky opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100">
                {t("nav.go")} →
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
